<template>
  <div style="background:#fff; padding:20px;">
    <!-- 顶部搜索区 -->
    <div style="margin-bottom: 20px">
      <a-form layout="inline">
        <a-form-item label="用户ID">
          <a-input-number
            v-model="query.userId"
            :min="1"
            placeholder="请输入用户ID"
            style="width: 140px"
          />
        </a-form-item>
        <a-form-item label="流水方向">
          <a-select
            v-model="query.flowType"
            placeholder="全部"
            allow-clear
            style="width: 120px"
          >
            <a-select-option :value="1">入账</a-select-option>
            <a-select-option :value="2">出账</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="业务类型">
          <a-select
            v-model="query.bizType"
            placeholder="全部"
            allow-clear
            style="width: 160px"
          >
            <a-select-option :value="1">商家支付出账</a-select-option>
            <a-select-option :value="2">工单结算入账</a-select-option>
            <a-select-option :value="3">提现成功出账</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="时间区间">
          <a-range-picker
            v-model="query.timeRange"
            :show-time="{ format: 'HH:mm:ss' }"
            format="YYYY-MM-DD HH:mm:ss"
            value-format="YYYY-MM-DD HH:mm:ss"
            :placeholder="['起始时间', '结束时间']"
            style="width: 380px"
          />
        </a-form-item>
        <a-form-item>
          <a-button type="primary" icon="search" @click="handleSearch">查询</a-button>
          <a-button style="margin-left:10px" icon="reload" @click="handleReset">重置</a-button>
        </a-form-item>
      </a-form>
    </div>

    <!-- 列表 -->
    <a-table
      :columns="columns"
      :data-source="data"
      rowKey="id"
      :loading="loading"
      :pagination="pagination"
      @change="handleTableChange"
    >
      <!-- 流水方向 -->
      <span slot="flowType" slot-scope="text">
        <a-tag v-if="text === 1" color="green">入账</a-tag>
        <a-tag v-else-if="text === 2" color="red">出账</a-tag>
        <span v-else>-</span>
      </span>

      <!-- 业务类型 -->
      <span slot="bizType" slot-scope="text">
        <a-tag v-if="text === 1" color="orange">商家支付出账</a-tag>
        <a-tag v-else-if="text === 2" color="blue">工单结算入账</a-tag>
        <a-tag v-else-if="text === 3" color="purple">提现成功出账</a-tag>
        <span v-else>-</span>
      </span>

      <!-- 金额: 入账显示 +,出账显示 - -->
      <span slot="amount" slot-scope="text, record">
        <span :style="{ color: record.flowType === 1 ? '#52c41a' : '#f5222d' }">
          {{ record.flowType === 1 ? '+' : '-' }}{{ text }}
        </span>
      </span>
    </a-table>
  </div>
</template>

<script>
import moment from 'moment'
import { getFinancialFlowList } from '@/api'

export default {
  data() {
    return {
      data: [],
      loading: false,
      // 查询条件
      query: {
        userId: undefined,
        flowType: undefined,
        bizType: undefined,
        // a-range-picker v-model: [startStr, endStr] (因 valueFormat 已声明为字符串)
        timeRange: []
      },
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showTotal: (total) => `共 ${total} 条`
      },
      columns: [
        { title: 'ID', dataIndex: 'id', width: 80 },
        { title: '用户ID', dataIndex: 'userId', width: 100 },
        {
          title: '流水方向',
          dataIndex: 'flowType',
          width: 100,
          scopedSlots: { customRender: 'flowType' }
        },
        {
          title: '业务类型',
          dataIndex: 'bizType',
          width: 140,
          scopedSlots: { customRender: 'bizType' }
        },
        {
          title: '金额',
          dataIndex: 'amount',
          width: 120,
          scopedSlots: { customRender: 'amount' }
        },
        { title: '关联任务ID', dataIndex: 'taskId', width: 110 },
        { title: '备注', dataIndex: 'remark', ellipsis: true },
        { title: '创建时间', dataIndex: 'createdAt', width: 180 }
      ]
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    // 校验时间区间合法性
    // 仅在两端都填写时校验:起 ≤ 终,且跨度 ≤ 366 天
    // 返回 { ok, startTime, endTime, message }
    validateTimeRange() {
      const range = this.query.timeRange || []
      const start = range[0]
      const end = range[1]

      // 未填或填一半时,后端可接受单端缺省;前端不再前置拦截
      if (!start || !end) {
        return { ok: true, startTime: start || undefined, endTime: end || undefined }
      }

      const startMoment = moment(start)
      const endMoment = moment(end)
      if (!startMoment.isValid() || !endMoment.isValid()) {
        return { ok: false, message: '时间格式不合法' }
      }
      if (endMoment.isBefore(startMoment)) {
        return { ok: false, message: '结束时间不能早于起始时间' }
      }
      // 跨度校验:严格按天计算,与后端 366 天上限保持一致
      const diffDays = endMoment.diff(startMoment, 'days', true)
      if (diffDays > 366) {
        return { ok: false, message: '时间跨度不能超过 366 天' }
      }

      return {
        ok: true,
        startTime: startMoment.toISOString(),
        endTime: endMoment.toISOString()
      }
    },
    // 加载列表
    loadData() {
      // 先做时间区间校验,避免无效请求白跑一次
      const checked = this.validateTimeRange()
      if (!checked.ok) {
        this.$message.error(checked.message)
        return
      }

      this.loading = true
      const params = {
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
        userId: this.query.userId || undefined,
        flowType: this.query.flowType,
        bizType: this.query.bizType,
        startTime: checked.startTime,
        endTime: checked.endTime
      }
      getFinancialFlowList(params)
        .then(res => {
          // 兼容 MyBatis-Plus Page: { records, total }
          // 同时兼容 { list, total } 与数组返回
          const payload = (res && res.data) || {}
          if (payload.records) {
            this.data = payload.records
            this.pagination.total = payload.total || 0
          } else if (payload.list) {
            this.data = payload.list
            this.pagination.total = payload.total || 0
          } else if (Array.isArray(payload)) {
            this.data = payload
            this.pagination.total = payload.length
          } else {
            this.data = []
            this.pagination.total = 0
          }
        })
        .catch(err => {
          this.$message.error(err && err.message ? err.message : '加载失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    // 查询
    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },
    // 重置
    handleReset() {
      this.query = {
        userId: undefined,
        flowType: undefined,
        bizType: undefined,
        timeRange: []
      }
      this.pagination.current = 1
      this.loadData()
    },
    // 表格分页
    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.loadData()
    }
  }
}
</script>
